import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useWatch } from "react-hook-form";
import { Box, Button, Stack, Typography } from "@mui/material";
import InfoOutlineIcon from "@mui/icons-material/InfoOutlined";

import { BaseModal } from "component/Modal/BaseModal";
import LoadingButton from "component/Button/LoadingButton";
import { dateToHourMinute, safeHourMinuteToDate } from "utils/time";
import {
  EXTERNAL_SERVICE_CODE,
  ExternalServiceCodeType,
} from "types/externalService";
import { getCleaningSettingFields, recordToCleaningSetting } from "utils/robot/cleaningSetting/cleaningSetting";
import { CreateScheduleRequest } from "types/schedule";

import { ScheduleForm } from "./ScheduleForm";
import { MapOption, ScheduleFormInput, scheduleSchema } from "./type";
import { ScheduleItem } from "types/response/schedule";
import { alreadyExists } from "./helper";
import { useEffect } from "react";

type CreateModalProps = {
  exSrvCode: ExternalServiceCodeType;
  robotId: string;
  maps: MapOption[];
  open: boolean;
  onClose: () => void;
  onCreate: (input: CreateScheduleRequest) => void;
  isCreating: boolean;
  schedules: ScheduleItem[];
};

export const ScheduleCreateModal = ({
  exSrvCode,
  robotId,
  open,
  onClose,
  maps,
  schedules,
  onCreate,
  isCreating,
}: CreateModalProps) => {
  const {
    handleSubmit,
    control,
    formState: { errors },
    setError,
    setValue,
  } = useForm<ScheduleFormInput>({
    resolver: zodResolver(scheduleSchema),
    defaultValues: {
      scheduleName: `スケジュール`,
      dayOfWeek: "1",
      time: safeHourMinuteToDate(22, 0),
      mapId: maps[0].mapId,
      cleaningSettings: getCleaningSettingFields(exSrvCode).reduce(
        (acc, cur) => {
          acc[cur.type] = cur.defaultValue;
          return acc;
        },
        {} as Record<string, string>
      ),
    },
  });

  const selectedCleanType = useWatch({
    control,
    name: "cleaningSettings.cleanType",
  });
  const selectedRoute = useWatch({
    control,
    name: "cleaningSettings.route",
  });

  useEffect(() => {
    if (exSrvCode !== EXTERNAL_SERVICE_CODE.DREAME) return;

    if (
      ["1", "5"].includes(selectedCleanType) &&
      ["2", "3"].includes(selectedRoute)
    ) {
      setValue("cleaningSettings.route", "1");
    }
  }, [exSrvCode, setValue, selectedCleanType, selectedRoute]);

  const onSubmit = async (data: ScheduleFormInput) => {
    const { hour, minute } = dateToHourMinute(data.time);
    const dayNum = Number(data.dayOfWeek);
    if (isNaN(dayNum) || dayNum < 0 || 6 < dayNum) {
      setError("dayOfWeek", { type: "manual", message: "不正な値です" });
      return;
    }

    if (alreadyExists({ dayOfWeek: dayNum, hour, minute }, schedules)) {
      setError("time", {
        type: "manual",
        message: "すでにスケジュールが設定されています",
      });
      return;
    }

    onCreate({
      ...data,
      hour,
      minute,
      dayOfWeek: dayNum,
      cleaningSettings: recordToCleaningSetting(data.cleaningSettings),
      robotId,
    });
  };

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title={
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <InfoOutlineIcon color="primary" sx={{ fontSize: 28 }} />
          <Typography>スケジュール追加</Typography>
        </Box>
      }
      children={
        <form onSubmit={handleSubmit(onSubmit)} id="schedule-form">
          <ScheduleForm
            exSrvCode={exSrvCode}
            errors={errors}
            control={control}
            maps={maps}
          />
        </form>
      }
      actions={
        <Stack direction={"row"} spacing={2}>
          <Button
            disabled={isCreating}
            variant="outlined"
            onClick={onClose}
            autoFocus
          >
            キャンセル
          </Button>
          <LoadingButton
            isLoading={isCreating}
            variant="contained"
            type="submit"
            form="schedule-form"
            sx={{ minWidth: "120px" }}
          >
            追加
          </LoadingButton>
        </Stack>
      }
    />
  );
};
